// src/pages/BlogDetailPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Calendar, User, Clock, ArrowLeft, Share2, Bookmark, Printer, FileText } from 'lucide-react';
import Button from '../components/ui/Button';
import client from '../utils/sanityClient';
import { getSafeImageUrl, SafeImage } from '../utils/imageUtils';

const BlogDetailPage = () => {
  // Get slug from URL params
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract slug and handle fallback for direct navigation cases
  const { slug } = params;
  
  // Debug URL params
  console.log('Blog Detail Page - Path:', location.pathname);
  console.log('Blog Detail Page - Params:', params);
  console.log('Blog Detail Page - Extracted slug:', slug);
  
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedContent, setProcessedContent] = useState([]);
  const [contentProcessed, setContentProcessed] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  // Extract slug manually from URL path if params fail
  useEffect(() => {
    // This is a backup in case useParams doesn't work properly
    if (!slug && location.pathname) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2 && pathParts[0] === 'blogs') {
        const manualSlug = pathParts[1];
        console.log('Manually extracted slug from path:', manualSlug);
        // We can't update the slug from useParams, but we can fetch using this slug
        fetchBlogPost(manualSlug);
      } else {
        setError(new Error('Invalid blog URL'));
        setLoading(false);
      }
    } else if (slug) {
      fetchBlogPost(slug);
    } else {
      setError(new Error('No blog post slug provided'));
      setLoading(false);
    }
  }, [location.pathname, slug]);

  // Function to fetch blog post
  const fetchBlogPost = async (slugToFetch) => {
    console.log('Fetching blog post for slug:', slugToFetch);
    let isMounted = true;
    
    try {
      // GROQ query to fetch a single blog post by slug
      const query = `*[_type == "post" && slug.current == $slug][0]{
        _id,
        title,
        slug,
        excerpt,
        publishedAt,
        readTime,
        category,
        "author": author->name,
        "authorRole": author->role,
        "authorBio": author->bio,
        mainImage,
        content,
        "relatedPosts": relatedPosts[]->{ 
          _id, 
          title, 
          "slug": slug.current
        }
      }`;
      
      const data = await client.fetch(query, { slug: slugToFetch });
      
      console.log('Query result:', data);
      
      if (isMounted) {
        if (data) {
          setPostData(data);
          
          // Set related posts if they exist
          if (data.relatedPosts && data.relatedPosts.length > 0) {
            setRelatedPosts(data.relatedPosts);
          }
          
          // Handle content processing
          processContent(data.content);
        } else {
          console.error('No post data found for slug:', slugToFetch);
          setError(new Error(`Blog post not found.`));
        }
      }
    } catch (err) {
      console.error("Error fetching blog post:", err);
      if (isMounted) {
        setError(err);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    
    return () => {
      isMounted = false;
    };
  };

  // Function to process content
  const processContent = (content) => {
    if (!content || !Array.isArray(content)) {
      console.error("Invalid content structure:", content);
      setContentProcessed(true);
      return;
    }
    
    try {
      // Group list items together for better rendering
      const result = [];
      let currentList = null;
      let currentListType = null;
      let currentListItems = [];
      
      const processListItem = (block, index) => {
        if (currentListType !== block.listItem) {
          // Finish previous list if exists
          if (currentList && currentListItems.length > 0) {
            const listElement = currentListType === 'bullet' ? (
              <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-700">
                {currentListItems}
              </ul>
            ) : (
              <ol className="list-decimal pl-6 space-y-2 mb-4 text-gray-700">
                {currentListItems}
              </ol>
            );
            
            result.push(
              <div key={`list-${index}`} className="mb-4">
                {listElement}
              </div>
            );
          }
          
          // Start new list
          currentListType = block.listItem;
          currentListItems = [];
        }
        
        currentListItems.push(
          <li key={block._key || `li-${index}`} className="leading-relaxed">
            {block.children && block.children.map((child, i) => {
              if (child.marks && child.marks.includes('strong')) {
                return <strong key={`strong-${i}`} className="font-bold">{child.text}</strong>;
              } else if (child.marks && child.marks.includes('em')) {
                return <em key={`em-${i}`} className="italic">{child.text}</em>;
              } else {
                return child.text;
              }
            })}
          </li>
        );
      };
      
      content.forEach((block, index) => {
        // Handle list items
        if (block._type === 'block' && block.listItem) {
          processListItem(block, index);
        } else {
          // Finish any current list before proceeding
          if (currentListItems.length > 0) {
            const listElement = currentListType === 'bullet' ? (
              <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-700">
                {currentListItems}
              </ul>
            ) : (
              <ol className="list-decimal pl-6 space-y-2 mb-4 text-gray-700">
                {currentListItems}
              </ol>
            );
            
            result.push(
              <div key={`list-${index}`} className="mb-4">
                {listElement}
              </div>
            );
            
            currentList = null;
            currentListType = null;
            currentListItems = [];
          }
          
          // For paragraphs
          if (block._type === 'block' && block.style === 'normal') {
            result.push(
              <p key={block._key || `p-${index}`} className="text-gray-700 mb-4 leading-relaxed">
                {block.children && block.children.map((child, i) => {
                  const text = child.text || '';
                  
                  // Apply formatting based on marks
                  if (child.marks && child.marks.length > 0) {
                    if (child.marks.includes('strong')) {
                      return <strong key={`strong-${i}`} className="font-bold">{text}</strong>;
                    } else if (child.marks.includes('em')) {
                      return <em key={`em-${i}`} className="italic">{text}</em>;
                    } else if (child.marks.includes('underline')) {
                      return <u key={`u-${i}`} className="underline">{text}</u>;
                    }
                  }
                  
                  return text;
                })}
              </p>
            );
          }
          
          // For headings
          else if (block._type === 'block' && block.style && block.style.match(/^h[1-6]$/)) {
            const level = parseInt(block.style.substring(1), 10);
            let className = '';
            
            switch(level) {
              case 1:
                className = 'text-3xl font-bold mt-8 mb-4 text-gray-900';
                break;
              case 2:
                className = 'text-2xl font-bold mt-6 mb-3 text-gray-900';
                break;
              case 3:
                className = 'text-xl font-bold mt-5 mb-2 text-gray-900';
                break;
              default:
                className = 'text-lg font-bold mt-4 mb-2 text-gray-900';
            }
            
            result.push(
              <div key={block._key || `h-${index}`} className={className}>
                {block.children && block.children.map(child => child.text).join('')}
              </div>
            );
          }
          
          // For blockquotes
          else if (block._type === 'block' && block.style === 'blockquote') {
            result.push(
              <blockquote 
                key={block._key || `quote-${index}`} 
                className="border-l-4 border-secondary pl-4 italic my-6 py-2 text-gray-700"
              >
                {block.children && block.children.map(child => child.text).join('')}
              </blockquote>
            );
          }
          
          // For images
          else if (block._type === 'image' && block.asset) {
            try {
              result.push(
                <figure key={block._key || `img-${index}`} className="my-8">
                  <SafeImage
                    image={block}
                    alt={block.alt || 'Blog image'}
                    className="max-w-full h-auto mx-auto rounded-lg shadow-md"
                  />
                  {block.caption && (
                    <figcaption className="text-center text-sm text-gray-500 mt-2">
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            } catch(err) {
              console.error("Error rendering image:", err);
            }
          }
          
          // Default
          else if (block._type === 'block' && block.children) {
            result.push(
              <div key={block._key || `block-${index}`} className="mb-4 text-gray-700">
                {block.children.map(child => child.text).join('')}
              </div>
            );
          }
        }
      });
      
      // Add any remaining list items
      if (currentListItems.length > 0) {
        const listElement = currentListType === 'bullet' ? (
          <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-700">
            {currentListItems}
          </ul>
        ) : (
          <ol className="list-decimal pl-6 space-y-2 mb-4 text-gray-700">
            {currentListItems}
          </ol>
        );
        
        result.push(
          <div key="final-list" className="mb-4">
            {listElement}
          </div>
        );
      }
      
      setProcessedContent(result);
      setContentProcessed(true);
    } catch (err) {
      console.error("Error processing content:", err);
      setError(err);
      setContentProcessed(true);
    }
  };

  // Function to handle back navigation
  const goBack = () => {
    navigate('/blogs');
  };
  
  // Format date to match the original format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  // If loading, show a spinner
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }
  
  // If there's an error, show error message
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h1>
        <p className="text-gray-600 mb-6">{error.message || 'An error occurred while loading the blog post.'}</p>
        <Link to="/blogs" className="text-secondary hover:text-secondary-dark flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to All Articles
        </Link>
      </div>
    );
  }
  
  // If blog not found, show error message with link back to blogs
  if (!postData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h1>
        <p className="text-gray-600 mb-6">The article you are looking for does not exist or has been removed.</p>
        <Link to="/blogs" className="text-secondary hover:text-secondary-dark flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to All Articles
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      {/* Article Header Section */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={goBack}
              className="inline-flex items-center text-white/80 hover:text-white mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to All Articles
            </button>
            
            <div className="mb-4">
              <span className="bg-white/10 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                {postData.category}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{postData.title}</h1>
            
            {postData.excerpt && (
              <p className="text-xl text-white/90 mb-6">{postData.excerpt}</p>
            )}
            
            <div className="flex flex-wrap items-center text-white/80 text-sm mb-4">
              <div className="flex items-center mr-4 mb-2">
                <User className="h-4 w-4 mr-1" />
                <span>{postData.author}</span>
              </div>
              <div className="flex items-center mr-4 mb-2">
                <Calendar className="h-4 w-4 mr-1" />
                <span>{formatDate(postData.publishedAt)}</span>
              </div>
              <div className="flex items-center mb-2">
                <Clock className="h-4 w-4 mr-1" />
                <span>{postData.readTime || '10 min read'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Article Content */}
      <article className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Article Tools */}
            <div className="flex justify-end mb-8">
              <div className="flex space-x-4">
                <button 
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                  title="Share Article"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button 
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                  title="Save Article"
                >
                  <Bookmark className="h-5 w-5" />
                </button>
                <button 
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                  title="Print Article"
                  onClick={() => window.print()}
                >
                  <Printer className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Article Image (if available) */}
            {postData.mainImage && (
              <div className="mb-8 rounded-lg overflow-hidden shadow-md">
                <SafeImage 
                  image={postData.mainImage}
                  alt={postData.mainImage.alt || postData.title}
                  className="w-full h-auto"
                />
                {postData.mainImage.caption && (
                  <div className="text-center text-sm text-gray-500 mt-2">
                    {postData.mainImage.caption}
                  </div>
                )}
              </div>
            )}
            
            {/* Article Content */}
            <div className="prose prose-lg max-w-none mb-12">
              {contentProcessed ? (
                <div>{processedContent}</div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-600">Processing content...</p>
                </div>
              )}
            </div>
            
            {/* Author Info */}
            <div className="mb-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mr-4">
                  {postData.author ? postData.author.charAt(0) : 'A'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{postData.author}</h3>
                  <p className="text-gray-600 mb-3">{postData.authorRole}</p>
                  {postData.authorBio && (
                    <p className="text-gray-700">{postData.authorBio}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Related Articles */}
            {relatedPosts && relatedPosts.length > 0 && (
              <div className="border-t border-gray-200 pt-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedPosts.map((post, index) => (
                    <Link 
                      key={post._id || index}
                      to={`/blogs/${typeof post.slug === 'string' ? post.slug : post.slug}`}
                      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-center">
                        <FileText className="h-10 w-10 text-secondary mr-4" />
                        <h4 className="text-lg font-medium text-gray-900">{post.title}</h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
      
      {/* Newsletter Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Stay Updated with Our Legal Insights</h2>
          <p className="mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter to receive the latest articles, case analyses, and legal updates directly in your inbox.
          </p>
          
          <div className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder="Your email address"
                className="px-4 py-3 rounded-lg w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <Button 
                variant="accent"
                size="lg"
                className="whitespace-nowrap"
              >
                Subscribe
              </Button>
            </div>
            <p className="text-sm text-gray-300 mt-3">
              We respect your privacy. You can unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;